import { useState, useEffect } from 'react';
import { api, ProductRequestWithDetails, ProductRequestStatus } from '../api';

function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className={`card p-6 w-full ${sizeClasses[size]} relative z-10 animate-slide-up max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-ink-50">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const STATUS_OPTIONS: { value: ProductRequestStatus; label: string; color: string }[] = [
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'in_review', label: 'In Review', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'design_in_progress', label: 'Design In Progress', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'waiting_on_vendor', label: 'Waiting on Vendor', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'approved_to_create_shopify', label: 'Ready for Shopify', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'live', label: 'Live', color: 'bg-green-500/20 text-green-400' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-500/20 text-red-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-ink-600/50 text-ink-400' },
];

function getStatusInfo(status: ProductRequestStatus) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function AdminRequests() {
  const [requests, setRequests] = useState<ProductRequestWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<ProductRequestStatus | ''>('');

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ProductRequestWithDetails | null>(null);
  const [newStatus, setNewStatus] = useState<ProductRequestStatus>('submitted');
  const [adminNotes, setAdminNotes] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState('');

  useEffect(() => {
    loadRequests();
  }, [filterStatus]);

  const loadRequests = async () => {
    try {
      const { requests } = await api.getProductRequests(filterStatus || undefined);
      setRequests(requests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = (request: ProductRequestWithDetails) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAdminNotes(request.admin_notes || '');
    setUpdateError('');
    setShowDetailModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    setUpdateLoading(true);
    setUpdateError('');

    try {
      await api.updateProductRequestStatus(selectedRequest.id, newStatus, adminNotes);
      setShowDetailModal(false);
      loadRequests();
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update request');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">Product Requests</h1>
            <p className="text-ink-400 mt-1">Manage influencer product requests</p>
          </div>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="card p-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === ''
                  ? 'bg-accent-500 text-white'
                  : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
              }`}
            >
              All
            </button>
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status.value}
                onClick={() => setFilterStatus(status.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status.value
                    ? 'bg-accent-500 text-white'
                    : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>

        {/* Requests Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Request
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Influencer
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800">
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-ink-400">
                      No product requests found.
                    </td>
                  </tr>
                ) : (
                  requests.map((req) => {
                    const statusInfo = getStatusInfo(req.status);
                    return (
                      <tr key={req.id} className="hover:bg-ink-800/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="font-medium text-ink-100">#{req.id}</div>
                          {req.wants_designer ? (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                              Needs Designer
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-ink-200">{req.influencer_stage_name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-ink-200">{req.product_title}</div>
                          {req.requested_retail && (
                            <div className="text-sm text-ink-400">
                              Requested: ${req.requested_retail.toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-ink-300">
                          {formatDate(req.created_at)}
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => openDetailModal(req)}
                            className="btn-ghost text-sm"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={`Request #${selectedRequest?.id}`}
        size="lg"
      >
        {selectedRequest && (
          <div className="space-y-6">
            {updateError && (
              <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-3 text-accent-400 text-sm">
                {updateError}
              </div>
            )}

            {/* Request Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-ink-400">Influencer</label>
                <p className="text-ink-100 font-medium">{selectedRequest.influencer_stage_name}</p>
              </div>
              <div>
                <label className="text-sm text-ink-400">Product</label>
                <p className="text-ink-100 font-medium">{selectedRequest.product_title}</p>
              </div>
              <div>
                <label className="text-sm text-ink-400">Requested Retail</label>
                <p className="text-ink-100">
                  {selectedRequest.requested_retail
                    ? `$${selectedRequest.requested_retail.toFixed(2)}`
                    : '-'}
                </p>
              </div>
              <div>
                <label className="text-sm text-ink-400">Needs Designer</label>
                <p className="text-ink-100">{selectedRequest.wants_designer ? 'Yes' : 'No'}</p>
              </div>
            </div>

            {selectedRequest.requested_customization && (
              <div>
                <label className="text-sm text-ink-400">Customization Details</label>
                <p className="text-ink-200 mt-1 bg-ink-800 p-3 rounded-lg text-sm">
                  {selectedRequest.requested_customization}
                </p>
              </div>
            )}

            {selectedRequest.notes && (
              <div>
                <label className="text-sm text-ink-400">Influencer Notes</label>
                <p className="text-ink-200 mt-1 bg-ink-800 p-3 rounded-lg text-sm">
                  {selectedRequest.notes}
                </p>
              </div>
            )}

            {selectedRequest.design_assets_url && (
              <div>
                <label className="text-sm text-ink-400">Design Assets</label>
                <a
                  href={selectedRequest.design_assets_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-400 hover:text-accent-300 block mt-1"
                >
                  View Assets →
                </a>
              </div>
            )}

            {/* Ready for Shopify Checklist */}
            {(newStatus === 'approved_to_create_shopify' || selectedRequest.status === 'approved_to_create_shopify') && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <h4 className="font-medium text-emerald-400 mb-3">Ready for Shopify Creation</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-ink-200">
                  <li>Create product in Shopify Admin</li>
                  <li>Add tag: <code className="bg-ink-800 px-2 py-0.5 rounded text-accent-400">influencer_id:{selectedRequest.influencer_id_fk}</code></li>
                  <li>Add tag: <code className="bg-ink-800 px-2 py-0.5 rounded text-accent-400">vendor_catalog_product_id:{selectedRequest.vendor_catalog_product_id}</code></li>
                  <li>Save product - webhook will auto-link</li>
                </ol>
              </div>
            )}

            {/* Update Form */}
            <div className="border-t border-ink-800 pt-6">
              <h3 className="text-sm font-medium text-ink-200 mb-4">Update Status</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-300 mb-1">Status</label>
                  <select
                    className="input w-full"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as ProductRequestStatus)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-300 mb-1">Admin Notes</label>
                  <textarea
                    className="input w-full"
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Internal notes about this request..."
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={updateLoading}
                className="btn-primary"
              >
                {updateLoading ? 'Saving...' : 'Update Request'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
